import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Tooltip,
  Divider,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import SpaceDashboardRoundedIcon from '@mui/icons-material/SpaceDashboardRounded';
import AppsRoundedIcon from '@mui/icons-material/AppsRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import PeopleAltRoundedIcon from '@mui/icons-material/PeopleAltRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import FolderRoundedIcon from '@mui/icons-material/FolderRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '@mui/material/styles';

const drawerWidth = 240;
const collapsedWidth = 64;

const Layout = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isDrawerCollapsed, setIsDrawerCollapsed] = useState(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');
    return savedState ? JSON.parse(savedState) : false;
  });
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();

  const menuItems = [
    { text: 'Dashboard', icon: <SpaceDashboardRoundedIcon />, path: '/' },
    { text: 'Apps', icon: <AppsRoundedIcon />, path: '/apps' },
    { text: 'Groups', icon: <GroupsRoundedIcon />, path: '/groups' },
    { text: 'Users', icon: <PeopleAltRoundedIcon />, path: '/users' },
    { text: 'Files', icon: <FolderRoundedIcon />, path: '/files' },
    { text: 'Settings', icon: <SettingsRoundedIcon />, path: '/settings' },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerCollapse = () => {
    const newState = !isDrawerCollapsed;
    setIsDrawerCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileOpen) setMobileOpen(false);
  };

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Toolbar 
        sx={{ 
          px: 2, 
          minHeight: '64px !important',
          position: 'relative',
        }}
      >
        <IconButton
          onClick={handleDrawerCollapse}
          sx={{
            position: 'absolute',
            right: isDrawerCollapsed ? '50%' : 8,
            transform: isDrawerCollapsed ? 'translateX(50%)' : 'none',
            transition: theme.transitions.create(['right', 'transform'], {
              duration: theme.transitions.duration.shorter,
            }),
            p: 1,
            borderRadius: 1,
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          {isDrawerCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
      </Toolbar>

      <List component="nav" sx={{ px: 2, flex: 1 }}>
        {menuItems.map((item) => (
          <Tooltip
            key={item.text}
            title={isDrawerCollapsed ? item.text : ''}
            placement="right"
            arrow
          >
            <ListItem
              button
              onClick={() => handleNavigation(item.path)}
              selected={location.pathname === item.path}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                height: 56,
                py: 2,
                px: isDrawerCollapsed ? 2 : 2,
                justifyContent: isDrawerCollapsed ? 'center' : 'initial',
                transition: theme.transitions.create(['padding', 'background-color'], {
                  duration: theme.transitions.duration.shorter,
                }),
                '&.Mui-selected': {
                  backgroundColor: 'rgba(0, 113, 227, 0.08)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 113, 227, 0.12)',
                  },
                },
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: isDrawerCollapsed ? 0 : 2,
                  justifyContent: 'center',
                  color: location.pathname === item.path ? 'primary.main' : 'inherit',
                  transition: theme.transitions.create('margin', {
                    duration: theme.transitions.duration.shorter,
                  }),
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!isDrawerCollapsed && (
                <ListItemText
                  primary={item.text}
                  primaryTypographyProps={{
                    color: location.pathname === item.path ? 'primary' : 'inherit',
                    fontWeight: location.pathname === item.path ? 600 : 400,
                  }}
                />
              )}
            </ListItem>
          </Tooltip>
        ))}
      </List>
      <Divider sx={{ mx: 2 }} />
      <List component="nav" sx={{ px: 2, py: 1 }}>
        <Tooltip
          title={isDrawerCollapsed ? 'Logout' : ''}
          placement="right"
          arrow
        >
          <ListItem
            button
            onClick={handleLogout}
            sx={{
              borderRadius: 1,
              height: 56,
              py: 2,
              px: isDrawerCollapsed ? 2 : 2,
              justifyContent: isDrawerCollapsed ? 'center' : 'initial',
              transition: theme.transitions.create(['padding', 'background-color'], {
                duration: theme.transitions.duration.shorter,
              }),
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: isDrawerCollapsed ? 0 : 2,
                justifyContent: 'center',
                transition: theme.transitions.create('margin', {
                  duration: theme.transitions.duration.shorter,
                }),
              }}
            >
              <LogoutRoundedIcon />
            </ListItemIcon>
            {!isDrawerCollapsed && <ListItemText primary="Logout" />}
          </ListItem>
        </Tooltip>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${isDrawerCollapsed ? collapsedWidth : drawerWidth}px)` },
          ml: { sm: `${isDrawerCollapsed ? collapsedWidth : drawerWidth}px` },
          boxShadow: theme => theme.palette.mode === 'dark'
            ? '0 4px 30px rgba(0, 0, 0, 0.3)'
            : '0 4px 30px rgba(0, 0, 0, 0.1)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          backgroundColor: theme => theme.palette.mode === 'dark' 
            ? 'rgba(17, 25, 40, 0.75)'
            : 'rgba(255, 255, 255, 0.7)',
          borderBottom: '1px solid',
          borderColor: theme => theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.125)'
            : 'rgba(255, 255, 255, 0.3)',
          transition: theme.transitions.create(['width', 'margin', 'background-color', 'box-shadow'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          '&:hover': {
            boxShadow: theme => theme.palette.mode === 'dark'
              ? '0 4px 35px rgba(0, 0, 0, 0.35)'
              : '0 4px 35px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        <Toolbar 
          sx={{ 
            minHeight: '64px !important',
            '& .MuiIconButton-root': {
              color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.87)',
              '&:hover': {
                backgroundColor: theme => theme.palette.mode === 'dark' 
                  ? 'rgba(255, 255, 255, 0.08)'
                  : 'rgba(0, 0, 0, 0.04)',
              },
            },
          }}
        >
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuRoundedIcon />
          </IconButton>
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              fontWeight: 600, 
              color: theme => theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.95)' : 'rgba(0, 0, 0, 0.87)',
              letterSpacing: '0.02em',
            }}
          >
            MindOS Apps Portal
          </Typography>
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true,
        }}
        sx={{
          display: { xs: 'block', sm: 'none' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: drawerWidth,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRight: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Drawer
        variant="permanent"
        sx={{
          display: { xs: 'none', sm: 'block' },
          '& .MuiDrawer-paper': {
            boxSizing: 'border-box',
            width: isDrawerCollapsed ? collapsedWidth : drawerWidth,
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.05)'
              : 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(10px)',
            borderRight: theme.palette.mode === 'dark'
              ? '1px solid rgba(255, 255, 255, 0.1)'
              : '1px solid rgba(0, 0, 0, 0.1)',
            transition: theme.transitions.create('width', {
              duration: theme.transitions.duration.enteringScreen,
            }),
            overflowX: 'hidden',
          },
        }}
      >
        {drawer}
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${isDrawerCollapsed ? collapsedWidth : drawerWidth}px)` },
          ml: { sm: `${isDrawerCollapsed ? collapsedWidth : drawerWidth}px` },
          transition: theme.transitions.create(['margin', 'width'], {
            duration: theme.transitions.duration.enteringScreen,
          }),
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }} />
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
